import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { MdAutorenew } from 'react-icons/md';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import Select from '@/components/Select';
import { Tag } from '@/components/Tag';
import type { AgentApiJobSourcesItem } from '@/types/serializers';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import useFetchJobSources from '@/features/agent/linked_accounts/api/useFetchJobSources';
import { activeSyncRequestAtom } from '../atoms/syncRequestAtom';
import useCreateSharedJobPostSyncRequest from '../hooks/useCreateSharedJobPostSyncRequest';
import useFetchSharedJobPostSyncRequest from '../hooks/useFetchSharedJobPostSyncRequest';
import buildSyncRequestConditionTags from '../lib/buildSyncRequestConditionTags';
import type { SyncRequestSearchConditions } from '../lib/pickSyncRequestSearchConditions';
import SyncLoadingIllustration from './SyncLoadingIllustration';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchConditions: SyncRequestSearchConditions;
}

export default function SyncRequestDialog({ open, onOpenChange, searchConditions }: Props) {
  const [selectedJobSourceId, setSelectedJobSourceId] = useState<number | null>(null);
  const [activeSyncRequest, setActiveSyncRequest] = useAtom(activeSyncRequestAtom);
  const initializedForOpenRef = useRef(false);

  const encryptId = activeSyncRequest?.encryptId ?? null;

  const createMutation = useCreateSharedJobPostSyncRequest();
  const { reset: resetCreateMutation } = createMutation;

  const { data: jobSourcesData, isLoading: isJobSourcesLoading } = useFetchJobSources('database');

  const conditionTags = useMemo(
    () => buildSyncRequestConditionTags(searchConditions),
    [searchConditions]
  );

  const jobSourceOptions = useMemo(() => {
    return ((jobSourcesData?.data ?? []) as AgentApiJobSourcesItem[]).filter(
      (jobSource) =>
        jobSource.is_sync_request_supported &&
        !jobSource.is_deleted &&
        jobSource.status !== 'error' &&
        jobSource.status !== 'deleting'
    );
  }, [jobSourcesData]);

  const jobSourceSelectItems = useMemo(
    () =>
      jobSourceOptions.map((jobSource) => ({
        value: String(jobSource.id),
        label: `${jobSource.title}（${jobSource.source_display_type}）`,
      })),
    [jobSourceOptions]
  );

  useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      return;
    }
    if (initializedForOpenRef.current) return;
    if (encryptId) return;
    if (isJobSourcesLoading) return;

    initializedForOpenRef.current = true;
    resetCreateMutation();

    if (jobSourceOptions.length === 1) {
      setSelectedJobSourceId(jobSourceOptions[0].id);
    } else {
      setSelectedJobSourceId(null);
    }
  }, [open, encryptId, isJobSourcesLoading, jobSourceOptions, resetCreateMutation]);

  const { data: syncRequest, isError: isFetchError } = useFetchSharedJobPostSyncRequest(encryptId, {
    pollWhileStatusIn: ['processing'],
  });

  const status = syncRequest?.status;
  const isTerminal = status === 'completed' || status === 'failed';

  // 完了・失敗の通知は SyncRequestWatcher が担うため、このダイアログは閉じる
  useEffect(() => {
    if (isTerminal && open) onOpenChange(false);
  }, [isTerminal, open, onOpenChange]);

  const handleClose = () => onOpenChange(false);

  const handleSubmit = async () => {
    if (!selectedJobSourceId) return;
    try {
      const result = await createMutation.mutateAsync({
        searchConditions,
        jobSourceId: selectedJobSourceId,
      });
      setActiveSyncRequest({
        encryptId: result.encrypt_id,
        jobSourceTitle:
          jobSourceOptions.find((jobSource) => jobSource.id === selectedJobSourceId)?.title ?? null,
      });
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '取り込みリクエストに失敗しました');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[700px] max-w-[95vw] overflow-hidden rounded-sm shadow-layer3">
        <DialogHeader className="items-start space-y-xxs! px-lg! py-md!">
          <DialogTitle className="text-heading-xs text-main">この条件で同期する</DialogTitle>
          {!encryptId && (
            <DialogDescription className="text-body-xs text-main">
              同期先のサービスに存在する検索項目がある場合のみ、最大50件まで抽出可能です
            </DialogDescription>
          )}
        </DialogHeader>

        {!encryptId ? (
          <>
            <div className="flex flex-col gap-md p-lg">
              <div className="flex flex-col gap-xs">
                <p className="text-heading-xxs text-main">検索条件</p>
                {conditionTags.length > 0 ? (
                  <div className="flex flex-wrap gap-xs">
                    {conditionTags.map((tag) => (
                      <Tag
                        key={tag.key}
                        color="default"
                        size="md"
                        className="hidden-scrollbar max-w-full overflow-x-auto"
                      >
                        {tag.label}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-sub">表示できる検索条件がありません。</p>
                )}
              </div>

              <div className="flex flex-col gap-xs">
                <p className="text-heading-xxs text-main">同期するアカウント</p>
                {isJobSourcesLoading ? (
                  <div className="flex justify-center py-lg">
                    <Loading size="small" />
                  </div>
                ) : jobSourceSelectItems.length === 0 ? (
                  <p className="text-sm text-sub">選択可能な連携アカウントがありません。</p>
                ) : (
                  <Select
                    className="w-full"
                    items={jobSourceSelectItems}
                    value={selectedJobSourceId ? String(selectedJobSourceId) : undefined}
                    defaultLabel="連携アカウントを選択"
                    onValueChange={(value) => setSelectedJobSourceId(Number(value))}
                  />
                )}
              </div>
            </div>

            <DialogFooter className="justify-end gap-md px-lg! py-md! sm:space-x-0!">
              <div className="flex gap-md">
                <Button color="secondary" size="md" type="button" onClick={handleClose}>
                  キャンセル
                </Button>
                <Button
                  color="primary"
                  size="md"
                  type="button"
                  disabled={!selectedJobSourceId || createMutation.isPending}
                  onClick={handleSubmit}
                >
                  <MdAutorenew
                    className={`size-5 ${createMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  {createMutation.isPending ? 'リクエスト中...' : '同期を開始'}
                </Button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-md p-lg">
              <SyncLoadingIllustration />
              <p className="text-body-sm text-main">取り込み中です。しばらくお待ちください。</p>
              {isFetchError && (
                <p className="text-xs text-danger">進捗の取得に失敗しました。再試行しています…</p>
              )}
            </div>

            <DialogFooter className="justify-end gap-md px-lg! py-md! sm:space-x-0!">
              <Button color="secondary" size="md" type="button" onClick={handleClose}>
                閉じる
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
